import { TriangleAlert } from "lucide-react";
import { Link } from "react-router";

import { FormField, FormRow } from "@/shared/components/FormSection";
import {
  autoIngestStateOf,
  type IngestionRuleRecord,
} from "@/shared/domain/ingestionRules";
import { Goto } from "@/shared/navigation";

import styles from "./IngestedBy.module.css";
import { PausedBadge } from "./PausedBadge";

export interface IngestedByProps {
  rule: Pick<IngestionRuleRecord, "auto_ingest" | "invocation">;
  /** `row` fits a table cell; `section` fills a card on the rule's page. */
  layout: "row" | "section";
}

/** What a rule without an action expects to ingest its files instead. */
export const INGESTED_ELSEWHERE =
  "A trigger you manage, SDK calls from outside Roboto, or something else is expected to ingest these files.";

/**
 * What ingests the files a rule matches: its action, its action while paused,
 * or something besides the rule.
 */
export function IngestedBy({ rule, layout }: IngestedByProps) {
  const state = autoIngestStateOf(rule);
  const action = rule.invocation?.action;
  const inRow = layout === "row";

  if (state === "no-action" || action === undefined) {
    return (
      <div className={styles.stack} data-layout={layout}>
        <span className={styles.notByRule}>
          <TriangleAlert size={inRow ? 14 : 16} aria-hidden="true" />
          Something besides this rule
        </span>
        {!inRow && (
          <>
            <span className={styles.secondary}>{INGESTED_ELSEWHERE}</span>
            <span className={styles.muted}>
              If nothing ingests them, sessions holding these files never
              complete.
            </span>
          </>
        )}
      </div>
    );
  }

  if (state === "paused") {
    return (
      <div className={styles.stack} data-layout={layout}>
        <span className={styles.actionLine}>
          <code className={styles.actionName} data-layout={layout}>
            {action.name}
          </code>
          <PausedBadge />
        </span>
        <span className={styles.secondary}>
          {inRow
            ? "Action saved, not running. Sessions still wait."
            : "Not running. Sessions still wait until matching files are ingested."}
        </span>
      </div>
    );
  }

  if (inRow) {
    return (
      <code className={styles.actionName} data-layout={layout}>
        {action.name}
      </code>
    );
  }
  return (
    <FormRow>
      <FormField label="Action">
        <Link to={actionPath(action)} className={styles.actionName}>
          {action.name}
        </Link>
      </FormField>
      <FormField label="Runs as">
        <span>Organization service account</span>
      </FormField>
    </FormRow>
  );
}

/** The action's own page, as `Goto.action` navigates to it. */
function actionPath(action: {
  name: string;
  owner?: string | null;
  digest?: string | null;
}): string {
  const owner = action.owner ? `${action.owner}/` : "";
  const digest = action.digest ? `?digest=${action.digest}` : "";
  return `${Goto.route.Actions}/${owner}${action.name}${digest}`;
}
